import { useLocation } from "wouter";
import { useGetStudy } from "@/api/studio/studies/queries";
import { getNextRoute, StudyStep, StudyType } from "../utils/study-flow.config";
import { useGlobalStore } from "@/stores/global-store";

/**
 * useStudyNavigation hook
 *
 * Provides type-aware navigation for study flows.
 * Automatically determines the correct next step based on the study type.
 * Considers user features (like memoryEmotion) for conditional routing.
 *
 * @example
 * const { navigateToNext } = useStudyNavigation(projectId, studyId, "study-select");
 * navigateToNext();
 */
export const useStudyNavigation = (projectId: string, studyId: string, currentStep: StudyStep) => {
  const [, navigate] = useLocation();
  const { data: study } = useGetStudy(projectId, studyId);
  const entitlements = useGlobalStore((state) => state.entitlements);

  const studyType: StudyType = study?.type || "asset";
  const hasMemoryEmotion = entitlements.some((entitlement) => entitlement.name === "memoryEmotion");

  /**
   * Navigate to the next step in the flow based on your current step and study type
   */
  const navigateToNext = () => {
    const nextRoute = getNextRoute(studyType, currentStep, projectId, studyId, { hasMemoryEmotion });
    if (nextRoute) {
      navigate(nextRoute);
    }
  };

  /**
   * Navigate to a specific route
   */
  const navigateTo = (route: string) => {
    navigate(route);
  };

  /**
   * Check if navigation to next step is possible
   */
  const canNavigateNext = () => {
    const nextRoute = getNextRoute(studyType, currentStep, projectId, studyId, { hasMemoryEmotion });
    return nextRoute !== null;
  };

  /* Return */
  return {
    navigateToNext,
    navigateTo,
    canNavigateNext,
    studyType,
    hasMemoryEmotion,
  };
};
